// SpringXE central data source

export const LOGO_URL = "/images/logo.png";

// Royalty-free images from Unsplash (commercial use). Curated set:
// - Indian professionals / enterprise teams
// - AI / tech abstractions / dashboards
export const IMAGES = {
    heroPeople:
        "/images/hero/heroPeople.jpg", // Indian professional team meeting
    enterpriseTeam:
        "/images/hero/enterpriseTeam.jpg", // team collaboration
    indianProfessional:
        "/images/hero/indianProfessional.jpg", // Indian professional woman
    dashboardPreview:
        "/images/hero/dashboard-preview.png", // LMS learner dashboard screenshot
    trainingRoom:
        "/images/hero/trainingRoom.jpg", // classroom training
    dashboard:
        "/images/hero/dashboard.jpg", // analytics dashboard
    aiAbstract:
        "/images/hero/aiAbstract.jpg", // ai / gpt
    cloudNetwork:
        "/images/hero/cloudNetwork.jpg", // server / cloud
    cyber:
        "/images/hero/cyber.jpg", // cybersecurity lock
    coding:
        "/images/hero/coding.jpg", // code editor
    data:
        "/images/hero/data.jpg", // data screens
    workforce:
        "/images/hero/workforce.jpg", // workforce meeting
    onboarding:
        "/images/hero/onboarding.jpg", // group onboarding
    certification:
        "/images/hero/certification.jpg", // certification
    hiring:
        "/images/hero/hiring.jpg", // hiring
    assessment:
        "/images/hero/assessment.jpg", // assessments
    softSkills:
        "/images/hero/softSkills.jpg", // soft skills leadership
    devops:
        "/images/hero/devops.jpg", // devops
    testing:
        "/images/hero/testing.jpg", // testing quality
    methodologies:
        "/images/hero/methodologies.jpg", // planning workshop
    lms:
        "/images/hero/lms.jpg", // e-learning
    labs:
        "/images/hero/labs.jpg", // server room labs
    partners:
        "/images/hero/partners.jpg",
};

// Products
export const PRODUCTS = [
    {
        slug: "enterprise-lxp",
        title: "Enterprise LxP",
        tagline: "AI-native Learning Experience Platform",
        description:
            "A unified enterprise platform that curates personalized learning journeys, orchestrates skills, and measures capability outcomes at scale.",
        highlights: [
            "Adaptive learning paths",
            "Skills graph & taxonomy",
            "Enterprise SSO + SCIM",
            "Analytics dashboards",
        ],
        image: IMAGES.dashboard,
        longIntro:
            "Built ground-up for the enterprise, the SpringXE LxP consolidates learning delivery, skill measurement, hands-on labs, and business impact reporting into a single AI-native experience.",
        capabilities: [
            { title: "Skills Graph", desc: "A live enterprise ontology that maps every role, project, and capability to measurable skills." },
            { title: "Adaptive Journeys", desc: "AI curates the next best module for every learner, tuned to proficiency and role goals." },
            { title: "In-flow Coaching", desc: "Copilots for learners, mentors, and admins — active in the flow of work." },
            { title: "Enterprise Analytics", desc: "Board-ready dashboards connecting learning to workforce readiness KPIs." },
            { title: "Integrations", desc: "SSO, SCIM, HRIS, Slack, Teams, LMS bridges, xAPI, SCORM, and data lake pipes." },
            { title: "Governance", desc: "Enterprise-grade compliance — SOC 2, ISO-aligned, GDPR & DPDP ready." },
        ],
        outcomes: [
            "3.2x faster time-to-proficiency across role families",
            "42% reduction in learning administration hours",
            "Board-visible workforce readiness index",
        ],
    },
    {
        slug: "elearning",
        title: "eLearning",
        tagline: "Modular, immersive content library",
        description:
            "Interactive courseware built for enterprise consumption, covering technology, functional, and behavioral competencies.",
        highlights: [
            "10,000+ curated modules",
            "Micro-learning + long-form",
            "SCORM & xAPI ready",
            "Localization-friendly",
        ],
        image: IMAGES.lms,
        longIntro:
            "A modern, modular content library engineered for enterprise attention spans and enterprise governance — every asset instrumented, indexed, and mapped to a live skill.",
        capabilities: [
            { title: "Micro to Immersive", desc: "From 3-minute drills to multi-week deep-dive academies — one delivery fabric." },
            { title: "Practitioner Content", desc: "Content authored by industry practitioners, not generic instructional designers." },
            { title: "Interactive by Default", desc: "Every module ships with checks-for-understanding, code snippets, and simulations where relevant." },
            { title: "Standards Compliant", desc: "SCORM 2004, xAPI, cmi5 — plays nicely with your existing LMS if needed." },
            { title: "Localization Ready", desc: "Multi-language delivery with regional context and examples." },
            { title: "Continuous Refresh", desc: "Content refreshed on a rolling cadence to stay current with fast-moving stacks." },
        ],
        outcomes: [
            "Learner satisfaction consistently above 4.6/5",
            "40% lift in module completion vs legacy catalog",
            "Content-to-role fit visible in the LxP",
        ],
        topCourseCategories: [
            {
                category: "AI & Machine Learning",
                courses: [
                    { name: "Agentic AI", overview: "Design and orchestrate autonomous AI agents that plan, reason, and act across multi-step tasks." },
                    { name: "Agentic AI- Claude Code", overview: "Hands-on with Claude Code as an agentic pair-programmer for real engineering workflows." },
                    { name: "Agentic AI- Github Copilot", overview: "Use GitHub Copilot's agent mode to automate coding tasks end-to-end." },
                    { name: "RAG Architecture", overview: "Build retrieval-augmented generation pipelines that ground LLM answers in your own data." },
                    { name: "LangChain & RAG Applications", overview: "Compose LangChain chains, agents, and retrievers into production-ready RAG apps." },
                    { name: "MLOps with Generative AI", overview: "Operationalize generative AI models with CI/CD, monitoring, and lifecycle management." },
                    { name: "MCP", overview: "Connect LLMs to tools and data sources using the Model Context Protocol." },
                    { name: "Prompt Engineering", overview: "Craft reliable, high-quality prompts and evaluation techniques for LLM applications." },
                    { name: "Vibe Coding", overview: "Rapidly prototype software using AI-assisted, intent-driven development." },
                    { name: "Cursor", overview: "Master the Cursor AI code editor to accelerate everyday development work." },
                    { name: "AI for Product Manager", overview: "Apply AI tools and thinking to product discovery, roadmaps, and decision-making." },
                    { name: "Introduction to AI & ML on Google Cloud", overview: "Get started with core AI/ML services and workflows on Google Cloud Platform." },
                    { name: "Responsible AI and Governance", overview: "Frameworks for building and deploying AI responsibly, ethically, and compliantly." },
                ],
            },
            {
                category: "Programming & Development",
                courses: [
                    { name: "Python Programming", overview: "Build a strong foundation in Python for automation, data, and application development." },
                    { name: "Java Programming", overview: "Build a strong foundation in Java for object-oriented, enterprise, and application development." },
                    { name: "Github and Copilot", overview: "Streamline version control and boost productivity with GitHub and AI pair-programming." },
                ],
            },
            {
                category: "Cloud & Infrastructure",
                courses: [
                    { name: "AWS IAM", overview: "Secure AWS accounts with fine-grained identity, access, and permissions management." },
                    { name: "Azure Fundamentals", overview: "Core concepts of Azure cloud services, pricing, and governance for beginners." },
                    { name: "Terraform", overview: "Provision and manage cloud infrastructure as code with HashiCorp Terraform." },
                    { name: "Oracle Utilities Application Framework", overview: "Configure and extend applications built on Oracle's utilities application platform." },
                ],
            },
            {
                category: "DevOps & Security",
                courses: [
                    { name: "DevSecOps", overview: "Embed security practices directly into CI/CD pipelines and DevOps workflows." },
                ],
            },
            {
                category: "Product & Project Management",
                courses: [
                    { name: "Digital Product Management & Agile Essentials", overview: "Core practices for running agile product teams and digital delivery." },
                ],
            },
            {
                category: "Compliance, Quality & Governance",
                courses: [
                    { name: "ISO 9001: 2015", overview: "Understand and implement the ISO 9001:2015 quality management system standard." },
                    { name: "POSH", overview: "Prevention of Sexual Harassment awareness and compliance training for the workplace." },
                    { name: "Data Privacy", overview: "Practical grounding in data privacy regulations and organizational compliance." },
                ],
            },
            {
                category: "Business & Soft Skills",
                courses: [
                    { name: "Business Communication", overview: "Sharpen written and verbal communication for effective workplace collaboration." },
                    { name: "Advanced Business Communication", overview: "Elevate executive presence, negotiation, and high-stakes communication skills." },
                ],
            },
        ],
    },
    {
        slug: "cloud-labs",
        title: "Cloud Labs",
        tagline: "Hands-on infrastructure sandboxes",
        description:
            "Instant, disposable, cloud-native environments for engineers to practice on real stacks — from Kubernetes to Databricks.",
        highlights: [
            "Zero-setup provisioning",
            "Multi-cloud environments",
            "Time-boxed sessions",
            "Auto-graded outcomes",
        ],
        image: IMAGES.labs,
        longIntro:
            "Real infrastructure, real workloads, real skill signals. SpringXE Cloud Labs provisions ephemeral sandboxes that let engineers actually build — safely, at enterprise budget controls.",
        capabilities: [
            { title: "One-Click Provisioning", desc: "Learners get scoped environments in seconds — no cloud accounts to manage." },
            { title: "Multi-Cloud", desc: "AWS, Azure, GCP, plus specialty stacks like Databricks, Snowflake, and Kubernetes." },
            { title: "Auto-Grading", desc: "Objective, telemetry-driven scoring of lab outcomes — not just self-attestation." },
            { title: "Enterprise Controls", desc: "Per-cohort budgets, time-boxed sessions, audit logs, and IAM boundaries." },
            { title: "Guided Practice", desc: "Instructions, hints, and validators inline with the terminal — copilot-safe by default." },
            { title: "Live + Async", desc: "Run instructor-led sessions or self-paced labs on the same fabric." },
        ],
        outcomes: [
            "80% engineers report applied-skill confidence post-lab",
            "6x scale over instructor-only lab delivery",
            "Objective proof of hands-on proficiency",
        ],
    },
    {
        slug: "ai-assessments",
        title: "AI Powered Assessments",
        tagline: "Skill validation with intelligence",
        description:
            "Proctored, adaptive assessments that evaluate deep skill proficiency with AI-driven question banks and behavior analytics.",
        highlights: [
            "Adaptive question paths",
            "AI proctoring",
            "Role-fit scoring",
            "Anti-cheat integrity",
        ],
        image: IMAGES.assessment,
        longIntro:
            "Move beyond legacy MCQs. SpringXE assessments blend adaptive testing, applied challenges, and AI proctoring to produce a proficiency signal you can hire and deploy on.",
        capabilities: [
            { title: "Adaptive Testing", desc: "Question difficulty flexes to the candidate — sharper signal, shorter tests." },
            { title: "Applied Challenges", desc: "Case-style, code-style, and scenario-style prompts, not just recall." },
            { title: "AI Proctoring", desc: "Biometric identity, environment scan, and behavioral integrity signals." },
            { title: "Role-Fit Scoring", desc: "Scores mapped to your competency framework — not a generic percentile." },
            { title: "Bias Guardrails", desc: "Content and scoring reviewed for adverse impact and demographic fairness." },
            { title: "Panel Workflows", desc: "Reviewer dashboards, calibration tools, and structured decisioning." },
        ],
        outcomes: [
            "Reduce interview loops by up to 40%",
            "Improve hiring quality with proficiency-first evidence",
            "Audit-ready assessment integrity records",
        ],
    },
    {
        slug: "ai-coding",
        title: "AI Powered Coding Platform",
        tagline: "Engineering assessments reimagined",
        description:
            "Real-world engineering challenges with automated evaluation, plagiarism detection, and AI-assisted feedback loops.",
        highlights: [
            "40+ language runtimes",
            "Real-repo simulations",
            "Copilot-safe mode",
            "Panel review workflows",
        ],
        image: IMAGES.coding,
        longIntro:
            "Assess engineers on the work engineers actually do. SpringXE's coding platform blends multi-file repos, real toolchains, and AI-safe evaluation to produce signal that scales.",
        capabilities: [
            { title: "Real-Repo Challenges", desc: "Multi-file, real-world simulations — not toy problems." },
            { title: "40+ Runtimes", desc: "Every language and stack your teams actually use." },
            { title: "Copilot-Safe Mode", desc: "Detects and adjusts for AI-tool usage patterns — signal you can trust." },
            { title: "Automated Grading", desc: "Test suites, static analysis, and complexity checks bundled." },
            { title: "Plagiarism Detection", desc: "MOSS-class similarity plus editing telemetry." },
            { title: "Panel Reviews", desc: "Reviewers pair with code diffs, comments, and structured rubrics." },
        ],
        outcomes: [
            "Higher engineer-hire retention at 6 months",
            "3x faster tech screening at enterprise scale",
            "Clean audit trail for every submission",
        ],
    },
];

// Solutions
export const SOLUTIONS_TRAINING = [
    {
        slug: "corporate-training",
        title: "Corporate Training",
        description:
            "Enterprise-wide upskilling programs designed around measurable business outcomes and skills-of-tomorrow taxonomies.",
        image: IMAGES.enterpriseTeam,
        longIntro:
            "Build capability at enterprise scale through role-anchored corporate training programs that combine expert-led delivery, immersive labs, coaching, and outcome analytics.",
        pillars: [
            { title: "Strategy Alignment", desc: "Every program starts from your business plan and workforce map." },
            { title: "Modality Blend", desc: "Live cohorts, self-paced, labs, and coaching — orchestrated as one experience." },
            { title: "Outcome Instrumentation", desc: "Baselines, uplift measurement, and post-program capability signals." },
            { title: "Global Delivery", desc: "Multi-region, multi-language delivery on a single platform." },
        ],
        outcomes: [
            "Capability uplift measured against real skill baselines",
            "Business KPI attribution, not just completion rates",
            "One vendor, one operating model, one dashboard",
        ],
    },
    {
        slug: "new-hire-training",
        title: "New Hire Training",
        description:
            "Structured onboarding academies that accelerate time-to-productivity for graduate cohorts and lateral hires.",
        image: IMAGES.onboarding,
        longIntro:
            "Turn onboarding into a competitive advantage with structured academies for graduate cohorts, lateral hires, and campus-to-corporate journeys.",
        pillars: [
            { title: "Bootcamps", desc: "12- to 16-week bootcamps that deploy work-ready engineers, analysts, and consultants." },
            { title: "Role Simulations", desc: "Realistic project simulations mirroring your actual client and product work." },
            { title: "Buddy + Mentor Fabric", desc: "Digital and human coaching layered together to reduce ramp risk." },
            { title: "Readiness Signals", desc: "Certification and project readiness gates — not just attendance." },
        ],
        outcomes: [
            "Ramp time reduced by up to 60%",
            "Consistent quality across intake batches",
            "Higher new-hire engagement in first 90 days",
        ],
    },
    {
        slug: "certifications",
        title: "Certifications",
        description:
            "Officially aligned certification pathways across 25+ global bodies — with proctored delivery and readiness coaching.",
        image: IMAGES.certification,
        longIntro:
            "Certifications aligned with the world's leading technology and standards bodies — packaged with coaching, labs, and proctoring so pass rates match business expectations.",
        pillars: [
            { title: "Aligned Curriculum", desc: "Every prep track mapped to the official blueprint of the certifying body." },
            { title: "Proctored Delivery", desc: "AI-invigilated exams and integrity-preserving workflows." },
            { title: "Readiness Coaching", desc: "Test-taking coaches and diagnostic mock exams to close final gaps." },
            { title: "Voucher Management", desc: "Fully managed voucher, exam, and re-take administration." },
        ],
        outcomes: [
            "Pass rates consistently above global averages",
            "Fully managed exam operations for L&D teams",
            "Recognized credentials across enterprise portfolios",
        ],
    },
    {
        slug: "managed-ld",
        title: "Managed L&D",
        description:
            "Fully outsourced learning operations — from strategy and content to delivery, analytics, and impact reporting.",
        image: IMAGES.workforce,
        longIntro:
            "Offload the operational load of enterprise L&D to a partner that runs strategy, content, delivery, analytics, and reporting on a single platform.",
        pillars: [
            { title: "L&D as a Service", desc: "Program strategy, content curation, delivery, and analytics — one accountable partner." },
            { title: "Program Management", desc: "Dedicated PMs, learning architects, and reporting cadences." },
            { title: "Platform Included", desc: "SpringXE LxP included in the managed engagement — no separate license negotiation." },
            { title: "SLA-Driven", desc: "Delivery SLAs, business KPIs, and outcome guarantees." },
        ],
        outcomes: [
            "Free up L&D bandwidth for strategy, not ops",
            "Consolidate vendors and technology spend",
            "Predictable, board-visible learning outcomes",
        ],
    },
];

export const SOLUTIONS_TALENT = [
    {
        slug: "hire-train-deploy",
        title: "Hire–Train–Deploy",
        description:
            "Custom-built talent pipelines that hire, upskill, and deploy on niche technologies — with zero delivery risk.",
        image: IMAGES.hiring,
        longIntro:
            "Custom talent pipelines that sit inside your workforce plan — SpringXE hires against your archetype, trains on your stack, and deploys engineers ready to be productive from day one.",
        pillars: [
            { title: "Custom Sourcing", desc: "Sourcing calibrated to your archetype, region, diversity, and role priorities." },
            { title: "Stack-Specific Training", desc: "Bootcamps built on your exact technology and delivery patterns." },
            { title: "Deployment Ready", desc: "Engineers deploy with role-fit assessments, not resumes." },
            { title: "Performance SLA", desc: "SLA-backed engagement — you pay for outcomes, not intent." },
        ],
        outcomes: [
            "Time-to-deploy compressed to 12–16 weeks",
            "Retention lifted through role fit and clear career paths",
            "Predictable niche-skill supply for growth-critical programs",
        ],
    },
    {
        slug: "proctored-assessments",
        title: "Proctored Assessments",
        description:
            "High-integrity remote assessments powered by AI proctoring, biometric validation, and identity assurance.",
        image: IMAGES.assessment,
        longIntro:
            "Enterprise-grade proctored assessments that combine biometric identity, environment integrity, and behavior analytics — trusted for hiring, promotion, and certification decisions.",
        pillars: [
            { title: "Identity Assurance", desc: "Face-match plus liveness plus document verification, layered." },
            { title: "Environment Scan", desc: "Room and desk verification, plus continuous background integrity signals." },
            { title: "Behavior Analytics", desc: "Anomaly detection tuned for anti-cheat while preserving fairness." },
            { title: "Audit Trail", desc: "Immutable event log per candidate — audit-ready records." },
        ],
        outcomes: [
            "Assessment integrity you can defend to auditors",
            "Global reach without global proctor operations",
            "Scale hiring and certifications without integrity trade-offs",
        ],
    },
    {
        slug: "niche-skill-projects",
        title: "Niche Skill Projects",
        description:
            "On-demand access to specialists across GenAI, cloud engineering, cybersecurity, and platform modernization.",
        image: IMAGES.aiAbstract,
        longIntro:
            "When niche capability is the bottleneck, plug in vetted specialists for time-bounded outcomes — with a productization layer that stays with your team.",
        pillars: [
            { title: "Vetted Specialists", desc: "Only practitioners who clear our applied assessments make it to bench." },
            { title: "Outcome Framing", desc: "Every engagement scoped to a business outcome, not a body-count." },
            { title: "Knowledge Transfer", desc: "Playbooks, artefacts, and coaching handovers ship with every project." },
            { title: "Fast Start", desc: "Kickoff within days, not months." },
        ],
        outcomes: [
            "Skip the specialist hiring cycle",
            "Bring internal teams up the curve alongside experts",
            "De-risk delivery of niche-skill programs",
        ],
    },
];

// Learning Tracks
export const LEARNING_TRACKS = [
    {
        slug: "genai",
        title: "GenAI",
        description: "LLMs, RAG, agentic systems, vector databases, foundation model tuning.",
        icon: "sparkles",
        image: IMAGES.aiAbstract,
        longIntro:
            "Enterprise-grade GenAI capability — from applied foundations to agentic systems, evaluation, and safe deployment inside regulated environments.",
        topics: [
            "LLM foundations and prompt patterns",
            "Retrieval Augmented Generation architectures",
            "Agentic systems and multi-agent orchestration",
            "Vector databases and semantic search",
            "Fine-tuning and instruction tuning",
            "Evaluations, safety, and guardrails",
            "GenAI product engineering",
            "Governance, cost, and observability",
        ],
        roles: ["GenAI Engineer", "AI Product Lead", "ML Platform Engineer", "AI Governance Analyst"],
    },
    {
        slug: "data-ai",
        title: "Data, Analytics & AI",
        description: "Modern data stack, ML engineering, MLOps and enterprise AI.",
        icon: "database",
        image: IMAGES.data,
        longIntro:
            "Modern data engineering and ML capability designed for the reality of enterprise data platforms — from ingestion to production ML.",
        topics: [
            "Modern data stack architecture",
            "Databricks, Snowflake, and lakehouse patterns",
            "Data engineering and streaming",
            "ML engineering and MLOps",
            "Analytics engineering and semantic layers",
            "Data governance and lineage",
            "Business analytics and visualization",
            "Feature stores and model serving",
        ],
        roles: ["Data Engineer", "ML Engineer", "Analytics Engineer", "Data Platform Lead"],
    },
    {
        slug: "cloud-devops",
        title: "Cloud & DevOps",
        description: "Kubernetes, IaC, platform engineering across AWS, Azure, GCP.",
        icon: "cloud",
        image: IMAGES.cloudNetwork,
        longIntro:
            "Cloud engineering and modern operations — from Kubernetes and platform engineering to SRE and cloud-native reliability at scale.",
        topics: [
            "Kubernetes and container platforms",
            "Infrastructure as Code (Terraform, Pulumi)",
            "Platform engineering and internal developer platforms",
            "CI/CD and progressive delivery",
            "Observability and SRE",
            "FinOps and cloud cost engineering",
            "Multi-cloud architecture",
            "Cloud security posture management",
        ],
        roles: ["DevOps Engineer", "SRE", "Platform Engineer", "Cloud Architect"],
    },
    {
        slug: "cybersecurity",
        title: "Cybersecurity",
        description: "Zero-trust, SOC engineering, cloud security, application security.",
        icon: "shield",
        image: IMAGES.cyber,
        longIntro:
            "Enterprise-grade cybersecurity capability across offensive, defensive, cloud, application, and governance domains.",
        topics: [
            "Zero-trust architecture",
            "Cloud security (AWS, Azure, GCP)",
            "SOC engineering and threat hunting",
            "Application and API security",
            "Identity and access engineering",
            "Governance, risk, and compliance",
            "Purple-team simulations",
            "AI/LLM security",
        ],
        roles: ["Security Engineer", "SOC Analyst", "Cloud Security Architect", "AppSec Engineer"],
    },
    {
        slug: "software-engineering",
        title: "Software Engineering",
        description: "Full-stack, distributed systems, microservices, DDD, TDD.",
        icon: "code",
        image: IMAGES.coding,
        longIntro:
            "Deep engineering capability — from modern full-stack to distributed systems, domain design, and quality engineering practices.",
        topics: [
            "Modern full-stack (React, Node, Java, Python, Go)",
            "Distributed systems and microservices",
            "Domain-driven design",
            "Test-driven development and quality",
            "Event-driven architecture",
            "API design and contract testing",
            "Performance and reliability engineering",
            "Software architecture patterns",
        ],
        roles: ["Full-Stack Engineer", "Distributed Systems Engineer", "Tech Lead", "Software Architect"],
    },
    {
        slug: "applications-platforms",
        title: "Applications & Platforms",
        description: "SAP, Salesforce, ServiceNow, Oracle enterprise platforms.",
        icon: "layers",
        image: IMAGES.enterpriseTeam,
        longIntro:
            "Enterprise application platforms — from ERP and CRM to ITSM — delivered as role-anchored capability journeys.",
        topics: [
            "SAP S/4HANA and SAP BTP",
            "Salesforce (Sales, Service, Data)",
            "ServiceNow platform and modules",
            "Oracle Cloud applications",
            "Workday and HRIS platforms",
            "Enterprise integration patterns",
            "Business process modelling",
            "Platform-specific certification pathways",
        ],
        roles: ["Platform Consultant", "Techno-Functional Analyst", "Integration Architect", "Solution Consultant"],
    },
    {
        slug: "testing",
        title: "Testing",
        description: "SDET, automation, performance, security & AI-driven testing.",
        icon: "check-circle",
        image: IMAGES.testing,
        longIntro:
            "Modern quality engineering — SDET, test automation, performance, and AI-augmented testing for reliable enterprise software.",
        topics: [
            "SDET foundations and mindsets",
            "Web and mobile automation frameworks",
            "API and contract testing",
            "Performance and load engineering",
            "Chaos and resilience testing",
            "Security testing basics",
            "AI-augmented test authoring",
            "Quality metrics and observability",
        ],
        roles: ["SDET", "QA Automation Engineer", "Performance Engineer", "Quality Architect"],
    },
    {
        slug: "methodologies",
        title: "Methodologies & Frameworks",
        description: "Agile, SAFe, Scrum, Kanban, DevOps operating models.",
        icon: "compass",
        image: IMAGES.methodologies,
        longIntro:
            "Ways-of-working capability — from Agile and Scrum through SAFe, DevOps operating models, and product operating models.",
        topics: [
            "Agile and Scrum fundamentals",
            "SAFe for scaled delivery",
            "Kanban and flow engineering",
            "Product operating models",
            "DevOps operating models",
            "Value stream mapping",
            "OKRs and outcome-based planning",
            "Portfolio and program governance",
        ],
        roles: ["Agile Coach", "Scrum Master", "Delivery Lead", "Product Ops Lead"],
    },
    {
        slug: "soft-skills",
        title: "Soft Skills",
        description: "Leadership, communication, design thinking, business acumen.",
        icon: "users",
        image: IMAGES.softSkills,
        longIntro:
            "Behavioral capability — leadership, communication, business acumen, and design thinking — anchored on how your organization actually operates.",
        topics: [
            "Leadership at all levels",
            "Executive communication",
            "Design thinking and problem framing",
            "Business acumen and commercial fluency",
            "Client relationship management",
            "Presentation and storytelling",
            "Feedback and coaching skills",
            "Change management",
        ],
        roles: ["People Managers", "Senior ICs", "Client-Facing Teams", "Emerging Leaders"],
    },
];

// Technology Partners — real brand marks via Simple Icons CDN (CC0)
// Partners without a Simple Icons entry fall back to a premium letter chip.
const si = (slug, color = "E9CCFF") => `/logos/si/${slug}-${color}.svg`;

export const TECH_PARTNERS = [
    { name: "Amazon Web Services", slug: "aws", short: "AWS", hue: "#FF9900", logo: "/logos/aws.svg", mono: true },
    { name: "Microsoft", slug: "microsoft", short: "MS", hue: "#00A4EF", logo: "/logos/microsoft.svg", mono: true },
    { name: "Google Cloud", slug: "gcp", short: "GCP", hue: "#4285F4", logo: si("googlecloud", "FFFFFF") },
    { name: "SAP", slug: "sap", short: "SAP", hue: "#0FAAFF", logo: si("sap", "FFFFFF") },
    { name: "Red Hat", slug: "redhat", short: "RH", hue: "#EE0000", logo: si("redhat", "FFFFFF") },
    { name: "PMI", slug: "pmi", short: "PMI", hue: "#7A2E8C", logo: "/logos/pmi.png" },
    { name: "CompTIA", slug: "comptia", short: "CT", hue: "#C8202F", logo: si("comptia", "FFFFFF") },
    { name: "ISTQB", slug: "istqb", short: "ISTQB", hue: "#F3901D", logo: null },
    { name: "Scrum Alliance", slug: "scrum-alliance", short: "SA", hue: "#EF6C00", logo: si("scrumalliance", "FFFFFF") },
    { name: "ASCM", slug: "ascm", short: "ASCM", hue: "#0091DA", logo: "/logos/ascm.svg", mono: true },
    { name: "Siemens", slug: "siemens", short: "SI", hue: "#009999", logo: si("siemens", "FFFFFF") },
    { name: "Blockchain Training Alliance", slug: "blockchain-training-alliance", short: "BTA", hue: "#F5B027", logo: null },
    { name: "Android ATC", slug: "android-atc", short: "ATC", hue: "#3DDC84", logo: si("android", "FFFFFF") },
    { name: "PECB", slug: "pecb", short: "PECB", hue: "#1E88E5", logo: null },
    { name: "Oracle", slug: "oracle", short: "OR", hue: "#F80000", logo: "/logos/oracle.svg", mono: true },
    { name: "IBM", slug: "ibm", short: "IBM", hue: "#1F70C1", logo: "/logos/ibm.svg", mono: true },
    { name: "Dell EMC", slug: "dell-emc", short: "DELL", hue: "#007DB8", logo: si("dell", "FFFFFF") },
    { name: "MuleSoft", slug: "mulesoft", short: "MULE", hue: "#00A0DF", logo: null },
    { name: "Chef", slug: "chef", short: "CHEF", hue: "#F09820", logo: si("chef", "FFFFFF") },
    { name: "GitHub", slug: "github", short: "GH", hue: "#FFFFFF", logo: si("github", "FFFFFF") },
    { name: "Elastic", slug: "elastic", short: "EL", hue: "#00BFB3", logo: si("elastic", "FFFFFF") },
    { name: "Lightbend", slug: "lightbend", short: "LB", hue: "#4F86FF", logo: "/logos/lightbend.svg", mono: true },
    { name: "Mirantis", slug: "mirantis", short: "MR", hue: "#00A9E0", logo: "/logos/mirantis.svg", mono: true },
    { name: "ISC2", slug: "isc2", short: "ISC2", hue: "#7DB343", logo: "/logos/isc2.svg", mono: true },
    { name: "Check Point", slug: "check-point", short: "CP", hue: "#E51F19", logo: "/logos/check-point.svg", mono: true },
    { name: "Cloud Security Alliance", slug: "csa", short: "CSA", hue: "#0A5CFF", logo: "/logos/csa.png", mono: true },
    { name: "Snowflake", slug: "snowflake", short: "SF", hue: "#29B5E8", logo: si("snowflake", "FFFFFF") },
    { name: "Broadcom", slug: "broadcom", short: "BC", hue: "#CC092F", logo: si("broadcom", "FFFFFF") },
];

export const TRUSTED_ENTERPRISES = [
    { name: "Walmart", logo: null },
    { name: "Deloitte", logo: null },
    { name: "Accenture", logo: si("accenture", "FFFFFF") },
    { name: "Infosys", logo: si("infosys", "FFFFFF") },
    { name: "GE", logo: si("generalelectric", "FFFFFF") },
    { name: "VMware", logo: si("vmware", "FFFFFF") },
    { name: "Target", logo: si("target", "FFFFFF") },
    { name: "Mercedes-Benz", logo: null },
    { name: "RBS", logo: null },
    { name: "Wells Fargo", logo: si("wellsfargo", "FFFFFF") },
    { name: "IBM", logo: null },
    { name: "Dell", logo: si("dell", "FFFFFF") },
    { name: "Cisco", logo: si("cisco", "FFFFFF") },
    { name: "Nasdaq", logo: null },
    { name: "Intel", logo: si("intel", "FFFFFF") },
    { name: "Wipro", logo: si("wipro", "FFFFFF") },
    { name: "TCS", logo: si("tcs", "FFFFFF") },
    { name: "Thomson Reuters", logo: null },
    { name: "Salesforce", logo: null },
    { name: "Xerox", logo: null },
    { name: "Cognizant", logo: null },
    { name: "HSBC", logo: si("hsbc", "FFFFFF") },
    { name: "Sony", logo: si("sony", "FFFFFF") },
    { name: "Capgemini", logo: null },
    { name: "EY", logo: null },
];

export const INDUSTRIES = [
    { name: "Banking & Financial Services", desc: "Modernize core banking talent, cloud, and risk platforms.", icon: "landmark" },
    { name: "Insurance", desc: "Accelerate digital claims, actuarial and data science teams.", icon: "shield-check" },
    { name: "Technology & Product", desc: "Ship platform engineering, SRE and GenAI capability at scale.", icon: "cpu" },
    { name: "Healthcare & Life Sciences", desc: "Regulated learning for pharma, medtech, and digital health.", icon: "heart-pulse" },
    { name: "Retail & Consumer", desc: "Data-driven merchandising, digital commerce, and omni-CX.", icon: "shopping-bag" },
    { name: "Manufacturing & Industrial", desc: "Industry 4.0, IIoT, and digital thread capability programs.", icon: "factory" },
    { name: "Telecom & Media", desc: "5G, edge, and platform operations upskilling programs.", icon: "radio-tower" },
    { name: "Energy & Utilities", desc: "Grid modernization, cloud, and AI for energy transition.", icon: "zap" },
];

export const FAQS = [
    { q: "What makes SpringXE different from a traditional LMS?", a: "SpringXE is a Learning Experience Platform (LxP) built for enterprise scale. It combines a skills graph, adaptive learning, hands-on cloud labs, AI-driven assessments, and outcome analytics — all in one platform, unlike legacy content-catalog LMSs." },
    { q: "How do you measure the business impact of learning?", a: "We instrument every learner journey against a role-based skill taxonomy. Our platform ties completion, proficiency, and applied outcomes back to time-to-productivity, project readiness, and revenue-relevant KPIs." },
    { q: "Which enterprise integrations are supported?", a: "SSO (SAML, OIDC), SCIM, HRIS connectors (Workday, SuccessFactors), Slack, Teams, LMS bridges, xAPI and SCORM. Enterprise data can be piped into Snowflake, BigQuery, or your data lake." },
    { q: "Do you support hands-on labs across cloud providers?", a: "Yes. Cloud Labs provisions ephemeral, secure environments on AWS, Azure, GCP — including Databricks, Snowflake, Kubernetes, and specialty stacks — with per-learner budget and audit controls." },
    { q: "Can SpringXE also help us hire and deploy niche skills?", a: "Absolutely. Our Hire-Train-Deploy pods build custom talent pipelines. We source, assess, upskill, and deploy engineers on your stack — with performance SLAs." },
    { q: "Is SpringXE enterprise-ready for compliance and security?", a: "Yes — SOC 2 aligned processes, ISO-informed controls, GDPR & DPDP compliant. Full data residency options, encryption at rest and in transit, and enterprise SSO by default." },
];

export const TESTIMONIALS = [
    { quote: "SpringXE turned our GenAI upskilling program into a measurable capability engine. We ramped 1,400 engineers in two quarters with clear productivity outcomes.", name: "Ananya R.", role: "Chief Learning Officer, Global BFSI Leader" },
    { quote: "The LxP + Cloud Labs combo is what we wanted for years. Our data engineering readiness improved by 3x — measured on real project telemetry.", name: "Vikram S.", role: "VP Talent Development, Fortune 500 Tech" },
    { quote: "Their Hire-Train-Deploy pods filled critical niche roles inside 90 days. The proficiency-first approach really works at enterprise scale.", name: "Priya M.", role: "Director HR, Manufacturing Conglomerate" },
    { quote: "AI-powered assessments gave us the signal we could never get from traditional MCQs. Hiring quality up, false positives down significantly.", name: "Rohit K.", role: "Head of Engineering, Digital Bank" },
];

export const STATS = [
    { value: "12M+", label: "Learner journeys delivered" },
    { value: "500+", label: "Enterprise customers globally" },
    { value: "45+", label: "Countries served" },
    { value: "3.2x", label: "Faster time-to-proficiency" },
];

export const WHY_SPRINGXE = [
    { title: "Skills-first, not course-first", desc: "Every journey is anchored to a live enterprise skills graph — not a catalog of courses.", icon: "target" },
    { title: "Built for capability, not consumption", desc: "We instrument proficiency and applied outcomes, not just completion rates.", icon: "sparkles" },
    { title: "AI-native experience", desc: "Personalization, adaptive assessments, and copilots for learners and admins.", icon: "cpu" },
    { title: "Enterprise-grade platform", desc: "SSO, SCIM, audit, residency, and integrations — production-ready on day one.", icon: "shield" },
    { title: "Talent as a service", desc: "From upskilling to hire-train-deploy — one operating model across L&D and talent.", icon: "users" },
    { title: "Outcome accountable", desc: "SLA-backed programs measured on business KPIs, not activity metrics.", icon: "trending-up" },
];

export const PLATFORM_FEATURES = [
    { title: "Skills Graph & Taxonomy", desc: "Enterprise-grade skill ontology mapped to roles, projects, and workforce plans.", size: "lg", icon: "network" },
    { title: "Adaptive Learning Paths", desc: "AI curates the next best module for every learner in real time.", size: "sm", icon: "sparkles" },
    { title: "Cloud Labs", desc: "Instant sandboxes with real infrastructure — Kubernetes, Databricks, and more.", size: "sm", icon: "cloud" },
    { title: "Proctored Assessments", desc: "AI-invigilated assessments with identity assurance and anti-cheat integrity.", size: "md", icon: "eye" },
    { title: "Admin Analytics", desc: "Executive dashboards that connect learning to capability outcomes.", size: "md", icon: "bar-chart" },
    { title: "Copilots & Coaches", desc: "In-flow AI copilots for learners, mentors, and program owners.", size: "sm", icon: "message-square" },
];

export const JOURNEY_STEPS = [
    { step: "01", title: "Diagnose", desc: "Skill gap analysis mapped to your business strategy and workforce plans." },
    { step: "02", title: "Design", desc: "Role-anchored journeys — blended, immersive, measured against outcomes." },
    { step: "03", title: "Deliver", desc: "Deploy at scale on the LxP with cohorts, labs, assessments, and coaches." },
    { step: "04", title: "Demonstrate", desc: "Prove capability with proficiency signals, project readiness, and KPIs." },
];
